import {FC, useEffect, useMemo, useRef, useState} from 'react'
import axios from 'axios'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import {API_URL, brandName, toAbsoluteUrl} from '../../../../_metronic/helpers'
import {AppLoading} from '../../../../_metronic/helpers/components/AppLoading'
import {useAuth} from '../core/Auth'
import {removeSupportAuth} from '../core/AuthHelpers'
import {AuthModel} from '../core/_models'
import {getUserByToken} from '../core/_requests'
import {getEntryPath} from '../core/postLoginPath'
import {isStaff} from '../core/staffAccess'
import {
  StaffSsoAnswerError,
  StaffSsoParams,
  readStaffLandingPath,
  readStaffSsoParams,
  staffSsoError,
  staffSsoErrorCode,
} from '../core/staffSso'
import './SupportSsoCallback.css'

type StaffSsoState =
  | {status: 'loading' | 'success'; message: string}
  | {status: 'error'; code: string}

// The nonce is single use: a re-run of the effect (strict mode, a re-render) reuses the exchange in flight.
const exchangeRequests = new Map<string, Promise<AuthModel>>()

const exchangeStaffEntry = (params: StaffSsoParams): Promise<AuthModel> => {
  const key = `${params.userId}|${params.ts}|${params.nonce}|${params.sig}`
  let request = exchangeRequests.get(key)
  if (!request) {
    request = axios
      .post<AuthModel>(`${API_URL}/staff-sessions/exchange`, params)
      .then((response) => response.data)
    exchangeRequests.set(key, request)
    const forget = () => window.setTimeout(() => exchangeRequests.delete(key), 1000)
    request.then(forget, forget)
  }
  return request
}

/**
 * /staffsso: the CRM's «پنل» button signs the CRM user in as staff (docs/staff-access.md). The session is the usual
 * cookie session with the sections the CRM role grants; it lands on the page the CRM named (`next`), else on «کسب و کارها»
 * or the first granted page.
 */
const StaffSsoCallback: FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const {saveAuth, setCurrentUser} = useAuth()
  const [state, setState] = useState<StaffSsoState>({
    status: 'loading',
    message: 'در حال بررسی ورود از CRM…',
  })
  const params = useMemo(() => readStaffSsoParams(searchParams), [searchParams])
  const landing = useMemo(() => readStaffLandingPath(searchParams), [searchParams])
  // AuthProvider recreates saveAuth on every render; depending on it would restart the exchange.
  const authActionsRef = useRef({saveAuth, setCurrentUser})
  authActionsRef.current = {saveAuth, setCurrentUser}

  useEffect(() => {
    if (!params) {
      setState({status: 'error', code: 'INVALID_LINK'})
      return
    }

    let active = true
    let sessionSaved = false
    let redirectTimer: number | undefined
    // A support session kept in this tab would send its own token with every request instead of the new cookie.
    removeSupportAuth()

    exchangeStaffEntry(params)
      .then(async (auth) => {
        if (!active) return
        if (!auth?.tk || !isStaff(auth)) throw new StaffSsoAnswerError('Invalid staff SSO response')
        authActionsRef.current.saveAuth(auth)
        sessionSaved = true
        const {data: user} = await getUserByToken(auth.tk)
        if (!active) return
        if (!isStaff(user)) throw new StaffSsoAnswerError('The session is not a staff session')
        authActionsRef.current.setCurrentUser(user)
        setState({status: 'success', message: 'ورود انجام شد؛ در حال انتقال…'})
        redirectTimer = window.setTimeout(
          () => navigate(landing ?? getEntryPath(user), {replace: true}),
          350
        )
      })
      .catch((error) => {
        if (!active) return
        if (sessionSaved) authActionsRef.current.saveAuth(undefined)
        setState({status: 'error', code: staffSsoErrorCode(error, params.ts)})
      })

    return () => {
      active = false
      if (redirectTimer) window.clearTimeout(redirectTimer)
    }
  }, [navigate, params, landing])

  if (state.status !== 'error') {
    return (
      <AppLoading
        mode='fullscreen'
        message={state.message}
        detail={
          state.status === 'success'
            ? 'بخش‌هایی که نقش شما در CRM دارد آماده می‌شود'
            : 'اعتبار ورود و دسترسی‌های نقش شما در حال کنترل است'
        }
      />
    )
  }

  const error = staffSsoError(state.code, brandName)
  return (
    <main className='support-sso-page'>
      <section className='support-sso-card' role='alert' aria-labelledby='staffSsoTitle'>
        <img src={toAbsoluteUrl('/media/logos/default.svg')} alt={brandName} />
        <span className='support-sso-card__icon' aria-hidden='true'>
          <i className='las la-exclamation-triangle' />
        </span>
        <h1 id='staffSsoTitle'>{error.title}</h1>
        <p>{error.message}</p>
        <div className='support-sso-card__actions'>
          <button type='button' className='btn btn-primary' onClick={() => window.close()}>
            <i className='las la-arrow-right' /> بازگشت به CRM
          </button>
          <Link className='btn btn-light' to='/auth/login'>
            ورود معمولی به {brandName}
          </Link>
        </div>
        <small>
          کارکنان فقط با دکمه «پنل» در CRM وارد پنل می‌شوند و دسترسی‌شان را نقش CRM تعیین می‌کند.
        </small>
      </section>
    </main>
  )
}

export {StaffSsoCallback}
